import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cookie_consent_v1";

function updateGtagConsent(granted) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const state = granted ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
    functionality_storage: state,
    personalization_storage: state,
  });
}

/**
 * GDPR / ePrivacy cookie banner.
 * Works with Google Consent Mode v2 (defaults in index.html are "denied").
 * On accept → gtag('consent','update', granted). On reject → keeps denied.
 */
export default function CookieBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!saved) {
        // Delay to avoid competing with the hero on-load animation.
        const t = setTimeout(() => setOpen(true), 900);
        return () => clearTimeout(t);
      }
    } catch (e) {
      setOpen(true);
    }
  }, []);

  const persist = (status) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ status, at: new Date().toISOString() })
      );
    } catch (e) {}
  };

  const accept = () => {
    updateGtagConsent(true);
    persist("accepted");
    setOpen(false);
  };
  const reject = () => {
    updateGtagConsent(false);
    persist("rejected");
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="cookie-banner"
          role="dialog"
          aria-live="polite"
          aria-label="Preferenze cookie"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed left-3 right-3 md:left-6 md:right-auto md:max-w-md bottom-24 md:bottom-6 z-[70]"
        >
          <div className="relative rounded-2xl bg-navy text-white shadow-2xl ring-1 ring-white/10 p-4 sm:p-5">
            <button
              type="button"
              onClick={reject}
              aria-label="Chiudi e rifiuta"
              data-testid="cookie-banner-close"
              className="absolute right-3 top-3 text-white/60 hover:text-white transition"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className="grid place-items-center h-9 w-9 rounded-full bg-brand-orange/20 text-brand-orange shrink-0">
                <Cookie className="h-5 w-5" />
              </div>
              <div className="text-sm leading-relaxed text-white/85">
                <p className="font-display font-bold text-white text-base">
                  Cookie & Privacy
                </p>
                <p className="mt-1">
                  Usiamo cookie tecnici e, con il tuo consenso, cookie analitici
                  (Google Analytics) per migliorare il sito. Nessun dato viene
                  raccolto se non accetti.
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2">
              <button
                type="button"
                onClick={reject}
                data-testid="cookie-banner-reject"
                className="flex-1 rounded-xl border border-white/25 text-white/90 font-semibold px-4 py-2.5 text-sm hover:bg-white/10 transition"
              >
                Rifiuta
              </button>
              <button
                type="button"
                onClick={accept}
                data-testid="cookie-banner-accept"
                className="flex-1 rounded-xl bg-brand-orange text-white font-bold px-4 py-2.5 text-sm hover:brightness-110 transition btn-glow-orange"
              >
                Accetta
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
